import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { ExternalActionService, MapService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { NavigationService } from '@ebw-monorepo/navigation';
import { PERMISSION_NAME, PermissionService } from '@ebw-monorepo/utils';
import { API_URLS, ApiUrlService } from '../../../../../../../utils/src/lib/services/api-url.service';
import { MapRequestService } from '../../../../services/map-request.service';
import { IHttpMapGet } from './../../../../interfaces/httpReq.interface';
import { BookmarkService } from './../../../../services/bookmark.service';

@Component({
  selector: 'ebw-monorepo-bookmarks-form',
  templateUrl: './bookmarks-form.component.html',
  styleUrls: ['./bookmarks-form.component.scss']
})
export class BookmarksFormComponent implements OnInit, OnDestroy {
  @Input() title: string = 'map.sidebar.bookmark.new_views';
  @Input() id: number = null;
  @Input() layers: string = null;
  @Input() oldLayers: string = null;
  /** Panel configuration structure */
  configMap = null;

  /*flag permesso Visibilità query*/
  flagPermessoVisibilityBookmark: boolean = false;

  bookmarkForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl(''),
    central_point: new FormControl({ value: '', disabled: true }),
    projection: new FormControl({ value: '', disabled: true }),
    levels: new FormControl({ value: '', disabled: true }),
    visibility: new FormControl(false),
    date_modified: new FormControl('')
  });

  constructor(
    private httpSrv: MapRequestService,
    private mapService: MapService,
    private ebwModalSrv: EbwModalService,
    private i18nSrv: I18NService,
    private bookmarkSrv: BookmarkService,
    private route: ActivatedRoute,
    private router: Router,
    private navigation: NavigationService,
    private externalSrv: ExternalActionService,
    private _apiUrlService: ApiUrlService,
    private _permissionService: PermissionService
  ) {
    let state = this.router.getCurrentNavigation().extras.state;
    if (state) {
      this.title = state.title;
      this.layers = state.layers;
      this.id = state.id ? state.id : null;
      this.oldLayers = state.oldLayers ? state.oldLayers : null;
    }
  }

  ngOnInit(): void {
    /*Permesso visibilità bookmark*/
    this.flagPermessoVisibilityBookmark = this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.VISIBILITY_BOOKMARK);

    if (this.id) {
      this.getBookmark();
    } else {
      this.changeCenter(this.mapService.mapCenter[1], this.mapService.mapCenter[0]);
      let projection = MapService.EPSG_3857_KEY;
      this.bookmarkForm.get('projection').setValue(projection);
      if (this.layers) {
        this.bookmarkForm.get('levels').setValue(this.layers);
      }
    }

    //evento per il cambio di coordinate
    this.mapService.mapCenterChanged.subscribe((center: number[]) => {
      //cambio il centro solo se NON sono in modifica.
      // in modifica posso cambiare solo nome e descrizione
      if (!this.id) {
        this.changeCenter(center[1], center[0]);
      }
    });
  }

  getBookmark() {
    let req: IHttpMapGet = {
      url: this._apiUrlService.createUrl(API_URLS.MAP.BOOKMARK.GET) + '?ids=' + this.id
    };
    this.httpSrv.getMapRequest(req).subscribe({
      next: (resp: any) => {
        this.setForm(resp);
      },
      error: (error) => {
        this.ebwModalSrv.openErrorModal({
          title: 'common.error',
          content: 'common.data_retrieving_error'
        });
      }
    });
  }

  bookmarkHandler() {
    if (this.id) {
      this.updateBookmark();
    } else {
      this.addBookmark();
    }
  }

  addBookmark() {
    if (this.bookmarkForm.invalid) {
      const controls = this.bookmarkForm.controls;
      for (const name in controls) {
        if (controls[name].invalid) {
          controls[name].markAsTouched();
          controls[name].markAsDirty();
        }
      }
      return;
    }

    let data_creazione = new Date().toISOString();

    let req: IHttpMapGet = {
      url: this._apiUrlService.createUrl(API_URLS.MAP.BOOKMARK.ADD)
    };

    let body = {
      name: this.bookmarkForm.get('name').value,
      description: this.bookmarkForm.get('description').value,
      active_layers: this.bookmarkForm.get('levels').value,
      visibility: this.bookmarkForm.get('visibility').value,
      extent: this.mapService.openLayersMap.getView().calculateExtent(this.mapService.openLayersMap.getSize()),
      date_modified: data_creazione,
      date_created: data_creazione
    };

    this.httpSrv.postMapRequest(req, body).subscribe({
      next: (resp: any) => {
        this.ebwModalSrv
          .openSuccessModal({
            title: this.i18nSrv.getInstantValue(this.title),
            content: this.i18nSrv.getInstantValue('common.save_successful')
          })
          .afterClosed()
          .subscribe((s) => this.navigation.goBack());
      },
      error: (error) => {
        this.ebwModalSrv.openErrorModal({
          title: this.i18nSrv.getInstantValue(this.title),
          content: this.i18nSrv.getInstantValue('common.error_saving')
        });
      }
    });
  }

  //TODO Aggiungere controlli sul central_point
  setForm(data: any) {
    let data_mod = '';
    // adatto la data per stamparla all'utente
    if (data.date_modified) {
      data_mod = data.date_modified;
    } else {
      data_mod = data.date_created;
    }

    let formValue = {
      name: data.name,
      description: data.description,
      central_point: data.center.coordinates[0] + ' | ' + data.center.coordinates[1],
      projection: data.center.crs.properties.name,
      levels: data.active_layers,
      visibility: data.user_id == null,
      date_modified: data_mod
    };

    this.bookmarkForm.patchValue(formValue);
  }

  updateBookmark() {
    if (this.bookmarkForm.invalid) {
      const controls = this.bookmarkForm.controls;
      for (const name in controls) {
        if (controls[name].invalid) {
          controls[name].markAsTouched();
          controls[name].markAsDirty();
        }
      }
      return;
    }

    let data_mod = new Date().toISOString();

    let req: IHttpMapGet = {
      url: this._apiUrlService.createUrl(API_URLS.MAP.BOOKMARK.UPDATE)
    };

    let body = {
      id: this.id,
      visibility: this.bookmarkForm.get('visibility').value,
      name: this.bookmarkForm.get('name').value,
      description: this.bookmarkForm.get('description').value,
      date_modified: data_mod
    };

    this.httpSrv.postMapRequest(req, body).subscribe({
      next: (resp: any) => {
        this.ebwModalSrv
          .openSuccessModal({
            title: this.i18nSrv.getInstantValue(this.title),
            content: this.i18nSrv.getInstantValue('common.save_successful')
          })
          .afterClosed()
          .subscribe((s) => this.navigation.goBack());
      },
      error: (error) => {
        this.ebwModalSrv.openErrorModal({
          title: this.i18nSrv.getInstantValue(this.title),
          content: this.i18nSrv.getInstantValue('common.error_saving')
        });
      }
    });
  }

  private changeCenter(coord1, coord0) {
    let center = coord1 + ' | ' + coord0;
    this.bookmarkForm.get('central_point').setValue(center);
  }

  ngOnDestroy() {
    if (this.oldLayers) {
      this.bookmarkSrv.layersHandler(this.oldLayers, false);
    }

    this.externalSrv.nextViewerManager(false);
  }

  refreshLayer() {
    this.layers = this.bookmarkSrv.getCurrentLayer();
    this.bookmarkForm.get('levels').setValue(this.layers);
  }

  deleteView(id: number, name: string) {
    const confirmModal = this.ebwModalSrv.openConfirmModal({
      title: this.i18nSrv.getInstantValue('common.delete_title'),
      content: this.i18nSrv.getTranslateParameter('common.delete_message', name)
    });

    confirmModal.afterClosed().subscribe((result) => {
      if (result == true) {
        this.bookmarkSrv.deleteBookmark(id).subscribe({
          next: (resp: any) => {
            const success = this.ebwModalSrv.openSuccessModal({
              title: this.i18nSrv.getInstantValue('common.delete_title'),
              content: this.i18nSrv.getInstantValue('common.delete_title')
            });
            success.afterClosed().subscribe(() => {
              this.backToBookmarksList();
            });
          },
          error: (error) => {
            this.ebwModalSrv.openErrorModal({
              title: this.i18nSrv.getInstantValue('common.delete_title'),
              content: this.i18nSrv.getInstantValue('common.delete_error')
            });
          }
        });
      }
    });
  }

  backToBookmarksList() {
    this.router.navigate(['/home', { outlets: { sidebarAction: ['bookmarks'] } }], { relativeTo: this.route });
  }

  switchVisibility() {
    this.bookmarkForm.controls.visibility.setValue(!this.bookmarkForm.controls.visibility.getRawValue());
  }
}
